import React from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsLayout from './SettingsLayout';
import {
  CreditCard, Banknote, Smartphone, Check, Shield, Lock, Info,
} from 'lucide-react';

const PAYMENT_METHODS = [
  {
    id: 'paystack',
    name: 'Card (Paystack)',
    description: 'Pay with any Visa, Mastercard, or Amex card. Handled securely by Paystack — we never see your card details.',
    icon: CreditCard,
    color: {
      bg: 'bg-[#FFF5F6]',
      text: 'text-[#EFA9B6]',
    },
    badge: 'Instant',
  },
  {
    id: 'payshap',
    name: 'PayShap',
    description: 'Pay via your banking app using PayShap. Send directly to the pro — they confirm once the transfer clears.',
    icon: Smartphone,
    color: {
      bg: 'bg-[#F4EEFF]',
      text: 'text-[#AF8BD4]',
    },
    badge: 'Bank transfer',
  },
  {
    id: 'cash',
    name: 'Cash',
    description: 'Pay in person at your appointment. Available for services where the pro accepts cash on the day.',
    icon: Banknote,
    color: {
      bg: 'bg-green-50',
      text: 'text-green-600',
    },
    badge: 'In person',
  },
];

const PaymentMethodsPage = () => {
  const navigate = useNavigate();

  return (
    <SettingsLayout
      title="Payment Methods"
      subtitle="How you can pay on Skytone"
    >
      {/* Available methods */}
      <div className="space-y-3">
        {PAYMENT_METHODS.map((method) => {
          const Icon = method.icon;
          return (
            <div
              key={method.id}
              className="bg-white rounded-3xl border border-gray-100 p-5"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl ${method.color.bg} flex items-center justify-center ${method.color.text} shrink-0`}>
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-serif font-bold text-gray-900 text-base">
                      {method.name}
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${method.color.bg} ${method.color.text} uppercase tracking-wider`}>
                      {method.badge}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {method.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* How it's decided */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FCF8F9] flex items-center justify-center text-gray-500 shrink-0">
            <Info size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">
              Which methods can you use?
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Each professional chooses which payment methods they accept. You'll see their
              available options when you book — Card is always available, PayShap and Cash
              depend on the pro.
            </p>
          </div>
        </div>
      </div>

      {/* Security note */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-600 shrink-0">
            <Shield size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Your payments are secure</h3>
            <ul className="mt-2 space-y-1.5 text-xs text-gray-500">
              <li className="flex items-start gap-2">
                <Check size={12} className="text-green-500 shrink-0 mt-0.5" />
                <span>Card details go directly to Paystack, not through Skytone</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={12} className="text-green-500 shrink-0 mt-0.5" />
                <span>We never see or store your card number or CVV</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={12} className="text-green-500 shrink-0 mt-0.5" />
                <span>Protected under South African POPIA regulations</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Coming soon — saved cards */}
      <div className="flex items-start gap-2 p-4 bg-[#F4EEFF] rounded-2xl">
        <Lock size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600 leading-relaxed">
          <span className="font-bold text-gray-900">Coming soon:</span> save a card on your
          account for one-tap checkout. For now, you'll enter your card details each time you
          pay by card.
        </p>
      </div>
    </SettingsLayout>
  );
};

export default PaymentMethodsPage;